import { Grid, Typography } from "@mui/material";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { observer } from "mobx-react";
import React from "react";
import { dateGroupByGrains } from "../../../Grouping/GroupByTypes";
import type {
    DateGroupByConfiguration,
    DateGroupByGrain,
} from "../../../SignalsModels";
import ChartCardStore from "../../ChartCard/ChartCardStore";

type Props = {
    chartStore: ChartCardStore;
    /**
     * The grouping being configured. Owned by the coordinator, which derives it from the drawer's
     * vizOptions. This component holds no grouping state of its own.
     */
    draft: DateGroupByConfiguration;
    /** Reports an edited draft upward. Stable identity, so effects can depend on it. */
    onDraftChange: (draft: DateGroupByConfiguration) => void;
    validationMessage?: string;
};

type GrainOption = {
    id: DateGroupByGrain;
    label: string;
};

/**
 * How each grain is written for the user. A Record rather than a list so a grain added to the
 * contract without a label here fails the build. The order comes from `dateGroupByGrains`.
 */
const GRAIN_LABELS: Record<DateGroupByGrain, string> = {
    auto: "Auto",
    day: "Daily",
    week: "Weekly",
    month: "Monthly",
    quarter: "Quarterly",
};

const GRAIN_OPTIONS: GrainOption[] = dateGroupByGrains.map((grain) => ({
    id: grain,
    label: GRAIN_LABELS[grain],
}));

/**
 * Configures a date grouping: how wide each bucket is.
 *
 * A granularity picker rather than a range builder. The chart's own date filter decides which dates
 * are in play, and this decides how they are divided. `auto` leaves the choice to the backend, which
 * sizes buckets from the request's date range the same way an over-time chart does, so the width
 * follows the filter instead of going stale when the user changes it.
 */
export const DateGroupByEditor: React.FC<Props> = observer(
    ({ chartStore, draft, onDraftChange, validationMessage }) => {
        const selectedOption =
            GRAIN_OPTIONS.find((option) => option.id === draft.grain) ??
            GRAIN_OPTIONS[0];

        return (
            <Grid container direction="column" rowGap={0.75}>
                <Grid item>
                    <AcxSelectSingle
                        id={`${chartStore.id}-group-by-date-grain`}
                        inputLabel="Define Date Groups"
                        options={GRAIN_OPTIONS}
                        defaultValue={selectedOption}
                        onChange={(option?: GrainOption) => {
                            // The picker is not clearable, so a cleared value can only come from an
                            // interaction that leaves no grain. Auto is the documented default.
                            onDraftChange({
                                ...draft,
                                grain: option?.id ?? "auto",
                            });
                        }}
                        valueField="id"
                        labelField="label"
                        fullWidth
                        placeholder="Select interval"
                    />
                </Grid>
                <Grid item>
                    {/*
                     * Buckets are always cut on the interaction date, whatever date the chart is
                     * filtered on, so a chart filtered by arrival date can show a bucket its own
                     * filter appears to exclude. Said here because nothing else on the chart
                     * explains it.
                     */}
                    <Typography variant="body2" color="error.main">
                        Groups by date range for the interaction date only.
                    </Typography>
                </Grid>
                {validationMessage && (
                    <Grid item>
                        <Typography variant="body2" color="error.main">
                            {validationMessage}
                        </Typography>
                    </Grid>
                )}
            </Grid>
        );
    },
);

export default DateGroupByEditor;
